/**
 * ProgressPath Component - Salesforce SLDS Style
 *
 * Воронка со скруглёнными шевронами как в Salesforce Lightning Path.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Group,
  Text,
  Button,
  Tooltip,
  Loader,
  Modal,
  Stack,
  Textarea,
  Alert,
  Paper,
} from '@mantine/core';
import { IconCheck, IconAlertCircle, IconArrowDown, IconAlertTriangle, IconArrowRight } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import tokens from '../../../theme/designTokens';
import classes from './ProgressPath.module.css';

// Этапы сотрудничества с цветами
// Градиент зелёного от светлого к насыщенному для этапов message_sent -> classes_active
const COOPERATION_STAGES = [
  { code: 'new', name: 'Новый', shortName: 'Новый', color: '#0176D3' },                    // Синий
  { code: 'message_sent', name: 'Отправлено сообщение', shortName: 'Сообщ.', color: '#7DC681' },  // Светло-зелёный
  { code: 'call', name: 'Звонок', shortName: 'Звонок', color: '#5EBA62' },                 // Зелёный 1
  { code: 'meeting', name: 'Встреча', shortName: 'Встреча', color: '#48A84C' },            // Зелёный 2
  { code: 'master_class', name: 'Мастер-класс', shortName: 'МК', color: '#3D9141' },       // Зелёный 3
  { code: 'contract_signed', name: 'Заключение договора', shortName: 'Договор', color: '#2E7D32' }, // Зелёный 4
  { code: 'classes_active', name: 'Идут занятия', shortName: 'Занятия', color: '#1B5E20' }, // Насыщенный зелёный
  { code: 'classes_paused', name: 'Приостановка', shortName: 'Пауза', color: '#FFA000' },   // Жёлтый/оранжевый
  { code: 'contract_terminated', name: 'Договор расторгнут', shortName: 'Растор.', color: '#C62828' }, // Красный
  { code: 'cooperation_refused', name: 'Отказ', shortName: 'Отказ', color: '#800020' },              // Бордовый
];

const getStageState = (stageIndex, currentIndex) => {
  if (stageIndex < currentIndex) return 'completed';
  if (stageIndex === currentIndex) return 'current';
  return 'future';
};

/**
 * Компонент шеврона
 */
const StageChevron = ({ stage, index, currentIndex, totalStages, onStageClick, isAdmin, onAdminClick }) => {
  const state = getStageState(index, currentIndex);
  const isFirst = index === 0;
  const isLast = index === totalStages - 1;
  const isCurrent = state === 'current';

  // Для обычных пользователей - кликабельны только будущие этапы
  // Для админа - кликабельны все этапы кроме текущего
  const isClickableForUser = state === 'future';
  const isClickableForAdmin = isAdmin && !isCurrent;
  const isClickable = isClickableForUser || isClickableForAdmin;

  const handleClick = () => {
    if (isClickableForAdmin && onAdminClick) {
      // Клик админа для принудительного перехода
      onAdminClick(stage);
    } else if (isClickableForUser && onStageClick) {
      // Обычный клик по будущему этапу
      onStageClick(stage, index);
    }
  };

  // Цвета на основе состояния и индивидуального цвета этапа
  let bgColor, textColor;
  switch (state) {
    case 'completed':
    case 'current':
      // Используем индивидуальный цвет этапа
      bgColor = stage.color;
      textColor = 'white';
      break;
    default:
      bgColor = tokens.colors.path.incomplete; // Серый
      textColor = tokens.colors.path.incompleteText;
  }

  // Tooltip для админа
  const tooltipLabel = isClickableForAdmin
    ? 'Нажмите для принудительного перехода'
    : stage.name;

  return (
    <Tooltip label={tooltipLabel} position="top" withArrow>
      <Box
        className={classes.chevron}
        data-state={state}
        data-first={isFirst}
        data-last={isLast}
        onClick={handleClick}
        style={{
          '--chevron-bg': bgColor,
          '--chevron-color': textColor,
          cursor: isClickable ? 'pointer' : 'default',
        }}
      >
        <span className={classes.chevronContent}>
          {state === 'completed' ? (
            <IconCheck size={14} stroke={3} />
          ) : (
            stage.shortName
          )}
        </span>
      </Box>
    </Tooltip>
  );
};

/**
 * Основной компонент
 */
// Этапы для Individual организаций (только 3)
const INDIVIDUAL_STAGES = COOPERATION_STAGES.filter(s =>
  ['classes_active', 'classes_paused', 'cooperation_refused'].includes(s.code)
);

const ProgressPath = ({
  organizationId,
  currentStageCode = 'new',
  onStageChange,
  showNextButton = true,
  refreshKey = 0,
  userRole = null,  // Роль текущего пользователя
  organizationType = 'b2b',  // Тип организации
}) => {
  const isIndividual = organizationType === 'individual';
  const stages = isIndividual ? INDIVIDUAL_STAGES : COOPERATION_STAGES;
  const currentIndex = stages.findIndex(s => s.code === currentStageCode);
  const [loading, setLoading] = useState(false);
  const [requirements, setRequirements] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState(null);

  // Состояния для принудительного перехода (только ADMIN)
  const [forceModalOpen, setForceModalOpen] = useState(false);
  const [targetStage, setTargetStage] = useState(null);
  const [forceComment, setForceComment] = useState('');
  const [forceCommentError, setForceCommentError] = useState(null);
  const [forceLoading, setForceLoading] = useState(false);

  const isAdmin = userRole === 'ADMIN';

  // Получаем текущий и следующий этапы для модального окна
  const currentStage = stages[currentIndex];
  const nextStage = currentIndex < stages.length - 1 ? stages[currentIndex + 1] : null;

  // Проверяем, требуется ли комментарий для следующего этапа
  const requiresComment = requirements?.requires_comment ?? false;

  // Загружаем требования для следующего этапа
  useEffect(() => {
    if (!organizationId) return;

    const fetchRequirements = async () => {
      try {
        const response = await fetch(`/api/organizations/${organizationId}/stage-requirements`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setRequirements(data);
        }
      } catch (err) {
        console.error('Error fetching stage requirements:', err);
      }
    };

    fetchRequirements();
  }, [organizationId, currentStageCode, refreshKey]);

  const handleStageClick = (stage) => {
    // Клик по будущим этапам - показываем что нужно для перехода
    if (requirements && stage.code === requirements.next_stage) {
      if (!requirements.can_advance && requirements.missing_requirements.length > 0) {
        notifications.show({
          title: `Для перехода на этап «${stage.name}»`,
          message: requirements.missing_requirements.join('; '),
          color: 'orange',
          icon: <IconAlertCircle size={18} />,
          autoClose: 5000
        });
      }
    }
  };

  // Обработчик клика админа для принудительного перехода
  const handleAdminChevronClick = (stage) => {
    if (!isAdmin || stage.code === currentStageCode) return;

    setTargetStage(stage);
    setForceComment('');
    setForceCommentError(null);
    setForceModalOpen(true);
  };

  // Принудительный переход на этап (только для ADMIN)
  const handleForceStageChange = async () => {
    if (!forceComment.trim()) {
      setForceCommentError('Укажите причину принудительного перехода');
      return;
    }

    setForceLoading(true);
    try {
      const response = await fetch(`/api/organizations/${organizationId}/force-stage`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_stage: targetStage.code,
          comment: forceComment.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при смене этапа');
      }

      const result = await response.json();
      setForceModalOpen(false);

      notifications.show({
        title: 'Этап изменён',
        message: result.message,
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 3000
      });

      if (onStageChange) {
        onStageChange(result.new_stage, result.new_stage_name);
      }
    } catch (err) {
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 5000
      });
    } finally {
      setForceLoading(false);
    }
  };

  // Открытие модального окна подтверждения
  const handleButtonClick = () => {
    if (!organizationId) return;

    // Проверяем требования перед открытием модального окна
    if (requirements && !requirements.can_advance) {
      notifications.show({
        title: 'Невозможно перейти на следующий этап',
        message: requirements.missing_requirements.join('; '),
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 5000
      });
      return;
    }

    // Сбрасываем комментарий и ошибку
    setComment('');
    setCommentError(null);

    // Открываем модальное окно подтверждения
    setConfirmModalOpen(true);
  };

  // Подтверждение перехода на следующий этап
  const handleConfirmAdvance = async () => {
    // Проверяем комментарий если требуется
    if (requiresComment && !comment.trim()) {
      setCommentError('Укажите причину перехода на этот этап');
      return;
    }

    setLoading(true);
    try {
      const body = {};
      if (comment.trim()) {
        body.comment = comment.trim();
      }

      const response = await fetch(`/api/organizations/${organizationId}/advance-stage`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при переходе на следующий этап');
      }

      const result = await response.json();

      // Закрываем модальное окно
      setConfirmModalOpen(false);

      // Уведомляем об успехе
      notifications.show({
        title: 'Этап изменён',
        message: result.message,
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 3000
      });

      // Вызываем callback для обновления организации
      if (onStageChange) {
        onStageChange(result.new_stage, result.new_stage_name);
      }
    } catch (err) {
      console.error('Error advancing stage:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const hasNextStage = !isIndividual && currentIndex < stages.length - 1;
  const canAdvance = requirements?.can_advance ?? false;

  // Формируем tooltip для кнопки
  const getButtonTooltip = () => {
    if (!requirements) return null;
    if (requirements.can_advance) return 'Все требования выполнены';
    if (requirements.missing_requirements.length > 0) {
      return requirements.missing_requirements.join('\n');
    }
    return null;
  };

  return (
    <Box
      py="sm"
      px="md"
      style={{
        backgroundColor: 'transparent',
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap" gap="md">
        {/* Контейнер шевронов */}
        <Box className={classes.pathContainer}>
          {stages.map((stage, index) => (
            <StageChevron
              key={stage.code}
              stage={stage}
              index={index}
              currentIndex={currentIndex}
              totalStages={stages.length}
              onStageClick={handleStageClick}
              isAdmin={isAdmin}
              onAdminClick={handleAdminChevronClick}
            />
          ))}
        </Box>

        {/* Кнопка */}
        {showNextButton && hasNextStage && (
          <Tooltip
            label={getButtonTooltip()}
            position="bottom"
            multiline
            w={250}
            withArrow
            disabled={!getButtonTooltip()}
          >
            <Button
              variant="filled"
              color={canAdvance ? "green" : "gray"}
              size="compact-sm"
              leftSection={<IconCheck size={14} />}
              onClick={handleButtonClick}
              disabled={loading}
              styles={{
                root: {
                  flexShrink: 0,
                  height: '36px',
                  backgroundColor: canAdvance ? tokens.colors.path.complete : tokens.colors.neutral[4],
                  '&:hover': {
                    backgroundColor: canAdvance
                      ? tokens.colors.semantic.success.dark
                      : tokens.colors.neutral[5],
                  }
                }
              }}
            >
              Завершить этап
            </Button>
          </Tooltip>
        )}
      </Group>

      {/* Модальное окно подтверждения перехода */}
      <Modal
        opened={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title={requiresComment
          ? `Переход на этап «${nextStage?.name}»`
          : "Подтверждение перехода на следующий этап"
        }
        centered
        radius="lg"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {requiresComment
              ? `Для перехода на этап «${nextStage?.name}» необходимо указать причину.`
              : 'Вы уверены, что хотите перейти на следующий этап воронки?'
            }
          </Text>

          {/* Визуализация перехода - вертикально */}
          <Stack align="center" gap="xs" py="md">
            <Box
              px="lg"
              py="xs"
              style={{
                backgroundColor: currentStage?.color || tokens.colors.brand.primary,
                borderRadius: '8px',
                color: 'white',
                fontWeight: 500,
                fontSize: '14px',
                textAlign: 'center',
                minWidth: '180px'
              }}
            >
              {currentStage?.name || 'Текущий'}
            </Box>
            <IconArrowDown size={24} color={tokens.colors.neutral[5]} />
            <Box
              px="lg"
              py="xs"
              style={{
                backgroundColor: nextStage?.color || tokens.colors.semantic.success.primary,
                borderRadius: '8px',
                color: 'white',
                fontWeight: 500,
                fontSize: '14px',
                textAlign: 'center',
                minWidth: '180px'
              }}
            >
              {nextStage?.name || 'Следующий'}
            </Box>
          </Stack>

          {/* Поле комментария для этапов, требующих причину */}
          {requiresComment && (
            <Textarea
              label={nextStage?.code === 'classes_paused' ? 'Причина приостановки' : 'Причина расторжения'}
              placeholder={nextStage?.code === 'classes_paused'
                ? 'Укажите причину приостановки занятий...'
                : 'Укажите причину расторжения договора...'
              }
              required
              minRows={3}
              radius="xl"
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                if (commentError) setCommentError(null);
              }}
              error={commentError}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setConfirmModalOpen(false)}
              disabled={loading}
            >
              Отмена
            </Button>
            <Button
              variant="filled"
              color={requiresComment ? (nextStage?.code === 'contract_terminated' ? 'red' : 'orange') : 'green'}
              leftSection={loading ? <Loader size={14} color="white" /> : <IconCheck size={14} />}
              onClick={handleConfirmAdvance}
              loading={loading}
            >
              Подтвердить
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Модальное окно принудительного перехода (только для ADMIN) */}
      <Modal
        opened={forceModalOpen}
        onClose={() => setForceModalOpen(false)}
        title="Принудительный переход"
        centered
        radius="lg"
        size="md"
      >
        <Stack gap="md">
          {/* Предупреждение */}
          <Alert
            color="orange"
            icon={<IconAlertTriangle size={16} />}
            radius="md"
          >
            Вы переводите организацию на этап «{targetStage?.name}» минуя проверку требований.
            Это действие будет залогировано.
          </Alert>

          {/* Визуализация перехода */}
          <Group justify="center" gap="md" py="md">
            <Paper
              p="sm"
              radius="md"
              style={{
                backgroundColor: currentStage?.color || tokens.colors.brand.primary,
                minWidth: '140px',
                textAlign: 'center'
              }}
            >
              <Text c="white" fw={500} size="sm">{currentStage?.name}</Text>
            </Paper>
            <IconArrowRight size={24} color={tokens.colors.neutral[5]} />
            <Paper
              p="sm"
              radius="md"
              style={{
                backgroundColor: targetStage?.color || tokens.colors.semantic.success.primary,
                minWidth: '140px',
                textAlign: 'center'
              }}
            >
              <Text c="white" fw={500} size="sm">{targetStage?.name}</Text>
            </Paper>
          </Group>

          {/* Комментарий (обязательный) */}
          <Textarea
            label="Причина перехода"
            placeholder="Укажите причину принудительного перехода..."
            required
            minRows={3}
            radius="xl"
            value={forceComment}
            onChange={(e) => {
              setForceComment(e.target.value);
              if (forceCommentError) setForceCommentError(null);
            }}
            error={forceCommentError}
          />

          {/* Кнопки */}
          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setForceModalOpen(false)}
              disabled={forceLoading}
            >
              Отмена
            </Button>
            <Button
              color="orange"
              leftSection={forceLoading ? <Loader size={14} color="white" /> : <IconCheck size={14} />}
              onClick={handleForceStageChange}
              loading={forceLoading}
            >
              Подтвердить переход
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default ProgressPath;
